import type { ModelWithCapabilities } from './model-capability-filter';
import { modelProviderId } from './model-capability-filter';

export type ChatStorageModal = {
  name?: string;
  id?: number;
};

export type WebSearchMode = 'auto' | 'on' | 'off';

export type ChatStorageData = {
  modal?: ChatStorageModal;
  chatData?: unknown[];
  use_document?: boolean;
  has_document?: boolean;
  file_id?: string;
  file_type?: string;
  webSearchEnabled?: boolean;
  webSearchMode?: WebSearchMode;
  codeInterpreterEnabled?: boolean;
  thinkingEnabled?: boolean;
};

export function resolveWebSearchMode(data?: {
  webSearchMode?: unknown;
  webSearchEnabled?: unknown;
}): WebSearchMode {
  const mode = data?.webSearchMode;
  if (mode === 'auto' || mode === 'on' || mode === 'off') {
    return mode;
  }
  if (data?.webSearchEnabled === true) {
    return 'on';
  }
  return 'auto';
}

export function webSearchEnabledFromMode(mode: WebSearchMode): boolean {
  return mode === 'on';
}

export function persistWebSearchFlags(mode: WebSearchMode): {
  webSearchMode: WebSearchMode;
  webSearchEnabled: boolean;
} {
  return {
    webSearchMode: mode,
    webSearchEnabled: webSearchEnabledFromMode(mode),
  };
}

export type ChatStorageEntry = {
  provider_id: number;
  session_id?: string | null;
  data?: ChatStorageData;
};

export function readChatStorage(): ChatStorageEntry[] {
  try {
    const parsed = JSON.parse(localStorage.getItem('chat') || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function writeChatStorage(entries: ChatStorageEntry[]): void {
  localStorage.setItem('chat', JSON.stringify(entries));
}

export function getProviderEntry(
  entries: ChatStorageEntry[],
  providerId: number,
): ChatStorageEntry | undefined {
  return entries.find((entry) => entry.provider_id === providerId);
}

export function persistProviderModal(model: ModelWithCapabilities): {
  providerId: number;
  sessionId: string | null;
  previousName: string | null;
} {
  const providerId = modelProviderId(model);
  const entries = readChatStorage();
  const existing = getProviderEntry(entries, providerId);
  const previousName = existing?.data?.modal?.name ?? null;
  const nextModal = {
    ...(existing?.data?.modal || {}),
    name: model.name,
    id: model.id != null ? Number(model.id) : 0,
  };
  let nextEntries: ChatStorageEntry[];
  if (existing) {
    nextEntries = entries.map((entry) =>
      entry.provider_id === providerId
        ? {
            ...entry,
            data: {
              ...entry.data,
              modal: nextModal,
            },
          }
        : entry,
    );
  } else {
    nextEntries = [
      ...entries,
      {
        provider_id: providerId,
        session_id: null,
        data: {
          chatData: [],
          modal: nextModal,
        },
      },
    ];
  }
  writeChatStorage(nextEntries);
  const sessionId = getProviderEntry(nextEntries, providerId)?.session_id ?? null;
  return {
    providerId,
    sessionId: sessionId || null,
    previousName,
  };
}

export function persistProviderFeatureFlags(
  providerId: number,
  flags: Partial<
    Pick<
      ChatStorageData,
      'webSearchEnabled' | 'webSearchMode' | 'codeInterpreterEnabled' | 'thinkingEnabled'
    >
  >,
): ChatStorageEntry[] {
  const entries = readChatStorage();
  const nextEntries = entries.map((entry) =>
    entry.provider_id === providerId
      ? {
          ...entry,
          data: {
            ...entry.data,
            ...flags,
          },
        }
      : entry,
  );
  writeChatStorage(nextEntries);
  return nextEntries;
}

export function applyPickedChatModel(options: {
  model: ModelWithCapabilities;
  currentProviderId: number;
  setChatModel: (value: string) => void;
  setActiveProvider?: (id: number) => void;
}): { switchedProvider: boolean; sessionId: string | null; providerId: number } {
  const persisted = persistProviderModal(options.model);
  if (options.model.name && options.model.id != null) {
    options.setChatModel(`${options.model.name}|${options.model.id}`);
  }
  const switchedProvider = persisted.providerId !== Number(options.currentProviderId);
  if (switchedProvider) {
    options.setActiveProvider?.(persisted.providerId);
    if (options.model.provider_name) {
      localStorage.setItem('activeProvider', options.model.provider_name);
    }
  }
  return {
    switchedProvider,
    sessionId: persisted.sessionId,
    providerId: persisted.providerId,
  };
}
